/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// helper function to find the giftwrap key, expected qty and actual qty
function findGiftWrap (cart) {
    const GWID = 31647473827934;
    var gwKey = null;
    var gwQty = 0;
    var expectedGWinCart = 0;
    if (!cart.items) {
        return 0;
    }
    for (const line_item of cart.items) {
        if (line_item.id == GWID) {
            gwQty = line_item.quantity;
            gwKey = line_item.key;
        } else {
            if (line_item.properties) {
                if (line_item.properties['giftwrap'] == 'yes') {
                    expectedGWinCart += line_item.quantity;
                }
            }
        }
    }
    let giftWrap = {key: gwKey, expectedQty: expectedGWinCart, quantity: gwQty};
    return giftWrap;
}


// js-custom-elements.section/cart/gift-wrap.js
var GiftWrap = class extends HTMLInputElement {
    constructor () {
        super();
        this.giftWrapID = 31647473827934;
    }
    connectedCallback() {
        this.addEventListener("change", this._onValueChanged.bind(this));
    }
 
    async setGiftWrapProperty(property, quantity, key) {
        let new_property = { 'giftwrap': property };
        const response1 = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
        let cartContent = await response1.json();
        for (const line_item of cartContent.items) {
            if (line_item.key == key) {
                if (line_item.properties) {
                    new_property = line_item.properties;
                    new_property['giftwrap'] = property;
                }
            }
        }
        let body = JSON.stringify({ quantity: quantity, properties: new_property, id: key });
        const response2 = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
            body: body,
            credentials: "same-origin",
            method: "POST",
            headers: {
                "Content-type": "application/json"
            }
        });
        cartContent = await response2.json();
        return cartContent;
    }
    
    async addGiftWraps(quantity) {
        this.dispatchEvent(new CustomEvent("line-item-quantity:change:start", { bubbles: true, detail: { newLineQuantity: quantity } }));
        const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
            body: JSON.stringify({ quantity: quantity, id: this.giftWrapID }),
            credentials: "same-origin",
            method: "POST",
            headers: {
                "Content-type": "application/json"
            }
        });
        const cartContent = response.json();
        this.dispatchEvent(new CustomEvent("line-item-quantity:change:end", { bubbles: true, detail: { cart: cartContent, newLineQuantity: quantity } }));
        return cartContent;
    }

    async changeGiftWrapCount(id, quantity) {
        this.dispatchEvent(new CustomEvent("line-item-quantity:change:start", { bubbles: true, detail: { newLineQuantity: quantity } }));
        const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
            body: JSON.stringify({ id, quantity, sections: ["mini-cart"] }),
            credentials: "same-origin",
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            }
        });
        
        const cartContent = await response.json();
        this.dispatchEvent(new CustomEvent("line-item-quantity:change:end", { bubbles: true, detail: { cart: cartContent, newLineQuantity: quantity } }));
        return cartContent;
    }


    async _onValueChanged() {
        this.disabled = true;
        var value = this.value.split(",");
        var quantity = Number(value[1]);
        var key = value[0];
        var property = 'no';
        if (this.checked) {
            property = 'yes';
        } else {
            property = 'no';
        }
        var cartContent = await this.setGiftWrapProperty(property, quantity, key);   
        var giftWrap = findGiftWrap(cartContent);
        // If there are zero giftwraps in the cart we need to add at least one. Otherwise if there
        // are already at least one giftwrap in the cart we need to update the amount to 
        // whatever qty is expected
        // += the item count with the number of items added to the cart so that the refresh event 
        // fires with the correct number of items added to the cart
        if (giftWrap.quantity == 0) { 
            let addResponse = await this.addGiftWraps(quantity); 
            cartContent['item_count'] += addResponse['quantity'];
            cartContent['total_price'] += addResponse['quantity'] * addResponse['price'];
        } else if (giftWrap.quantity != giftWrap.expectedQty) { 
            cartContent = await this.changeGiftWrapCount(giftWrap.key, giftWrap.expectedQty);
        } 
        this.dispatchEvent(new CustomEvent("cart:updated", {
            bubbles: true,
            detail: {
              cart: cartContent
            }
        }));
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
                cart: cartContent,
                replacementDelay: 750
            }
        }));
        this.disabled = false; 
    }

}
window.customElements.define("giftwrap-checkbox", GiftWrap, { extends: "input" });

document.body.addEventListener('docapp-discount-applied', async function (event) {
    const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
    let cartContent = await response.json();
    let discountedTotalPrice = 0;
    if (event.cart && event.cart.total_price) {
        discountedTotalPrice = event.cart.total_price;
    }
    document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
        bubbles: true,
        detail: {   
          cart: cartContent,
          discountedTotalPrice: discountedTotalPrice
        }
      }));
});


document.addEventListener('cart:updated', async function (event) {
    var cart = event.detail.cart; // Get the updated content of the cart
    const currentUrl = window.location.href;
    // turn off the cart icon wiggle on/off unless this is the cart page  
    if (cart.item_count == 0) {
        document.getElementById('cart-icon').classList.remove("wiggle");
    } else {
        document.getElementById('cart-icon').classList.add("wiggle");
    }
    if (currentUrl.match("friendsnyc.com/cart")) {
        document.getElementById('cart-icon').classList.remove("wiggle");
    }
    
    //are we on a collections page? if so update the offer badges
    if (currentUrl.match("friendsnyc.com/collections")) {
        const badges = document.getElementsByClassName("collection-offer-badge");
        for (let i = 0; i < badges.length; i++) {
            if (cart.original_total_price >= 5000 ) {
                badges[i].classList.add("collection-offer-badge__shipfreeat99");
                badges[i].classList.remove("collection-offer-badge__ship5under50");
                badges[i].innerHTML = "ships free @ $99";
            } else {
                let productPrice = parseInt(badges[i].getAttribute("price"));
                let expectedPrice = cart.original_total_price + productPrice;
                if (expectedPrice < 5000 ) {
                    badges[i].classList.remove("collection-offer-badge__shipfreeat99");
                    badges[i].classList.add("collection-offer-badge__ship5under50");
                    badges[i].innerHTML = "ships for $5 under $50";
                }
                if (expectedPrice >= 5000 ) {
                    badges[i].classList.add("collection-offer-badge__shipfreeat99");
                    badges[i].classList.remove("collection-offer-badge__ship5under50");
                    badges[i].innerHTML = "ships free @ $99";
                }
            }
        }
    }

    //reconcile giftwraps in cart
    var giftWrap = findGiftWrap(cart);
    if (giftWrap.quantity > 0 ) {
        if (giftWrap.quantity != giftWrap.expectedQty) {
            this.dispatchEvent(new CustomEvent("line-item-quantity:change:start", { bubbles: true, detail: { newLineQuantity: giftWrap.expectedQty } }));
            let id = giftWrap.key;
            let quantity = giftWrap.expectedQty;
            const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
                body: JSON.stringify({ id, quantity, sections: ["mini-cart"] }),
                credentials: "same-origin",
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                }
            });
            const cartContent = await response.json();
            this.dispatchEvent(new CustomEvent("line-item-quantity:change:end", { bubbles: true, detail: { cart: cartContent, newLineQuantity: giftWrap.expectedQty } }));
            document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
                bubbles: true,
                detail: {
                    cart: cartContent,
                    replacementDelay: 750
                }
            }));
        } 
    }
 });


 
/* ===================================================================================== @preserve =
 ___  _   _    _
/   || | | |  | |
\__  | | | |  | |  __
/    |/  |/_) |/  /  \_/\/
\___/|__/| \_/|__/\__/  /\_/
              |\
              |/
Ajaxinate
version v2.0.4
https://github.com/Elkfox/Ajaxinate
Copyright (c) 2017 Elkfox Co Pty Ltd
https://elkfox.com
MIT License
================================================================================================= */

var Ajaxinate = function (t) { var e = t || {}; this.settings = Object.assign({ method: "scroll", container: "#AjaxinateContainer", pagination: "#AjaxinatePagination", offset: 0, loadingText: "Loading", callback: null }, e), this.addScrollListeners = this.addScrollListeners.bind(this), this.addClickListener = this.addClickListener.bind(this), this.checkIfPaginationInView = this.checkIfPaginationInView.bind(this), this.stopMultipleClicks = this.stopMultipleClicks.bind(this), this.removeClickListener = this.removeClickListener.bind(this), this.removeScrollListener = this.removeScrollListener.bind(this), this.destroy = this.destroy.bind(this), this.containerElement = document.querySelector(this.settings.container), this.paginationElement = document.querySelector(this.settings.pagination), this.initialize() }; Ajaxinate.prototype.initialize = function () { this.containerElement && { click: this.addClickListener, scroll: this.addScrollListeners }[this.settings.method]() }, Ajaxinate.prototype.addScrollListeners = function () { this.paginationElement && (document.addEventListener("scroll", this.checkIfPaginationInView), window.addEventListener("resize", this.checkIfPaginationInView), window.addEventListener("orientationchange", this.checkIfPaginationInView)) }, Ajaxinate.prototype.addClickListener = function () { this.paginationElement && (this.nextPageLinkElement = this.paginationElement.querySelector("a"), this.clickActive = !0, void 0 !== this.nextPageLinkElement && null !== this.nextPageLinkElement && this.nextPageLinkElement.addEventListener("click", this.stopMultipleClicks)) }, Ajaxinate.prototype.stopMultipleClicks = function (t) { t.preventDefault(), this.clickActive && (this.nextPageLinkElement.innerText = this.settings.loadingText, this.nextPageUrl = this.nextPageLinkElement.href, this.clickActive = !1, this.loadMore()) }, Ajaxinate.prototype.checkIfPaginationInView = function () { var t = this.paginationElement.getBoundingClientRect().top - this.settings.offset, e = this.paginationElement.getBoundingClientRect().bottom + this.settings.offset; t <= window.innerHeight && e >= 0 && (this.nextPageLinkElement = this.paginationElement.querySelector("a"), this.removeScrollListener(), this.nextPageLinkElement && (this.nextPageLinkElement.innerText = this.settings.loadingText, this.nextPageUrl = this.nextPageLinkElement.href, this.loadMore())) }, Ajaxinate.prototype.loadMore = function () { this.request = new XMLHttpRequest, this.request.onreadystatechange = function () { if (4 === this.request.readyState && 200 === this.request.status) { var t = this.request.responseXML.querySelectorAll(this.settings.container)[0], e = this.request.responseXML.querySelectorAll(this.settings.pagination)[0]; this.containerElement.insertAdjacentHTML("beforeend", t.innerHTML), this.paginationElement.innerHTML = e.innerHTML, this.settings.callback && "function" == typeof this.settings.callback && this.settings.callback(this.request.responseXML), this.initialize() } }.bind(this), this.request.open("GET", this.nextPageUrl), this.request.responseType = "document", this.request.send() }, Ajaxinate.prototype.removeClickListener = function () { this.nextPageLinkElement.removeEventListener("click", this.stopMultipleClicks) }, Ajaxinate.prototype.removeScrollListener = function () { document.removeEventListener("scroll", this.checkIfPaginationInView), window.removeEventListener("resize", this.checkIfPaginationInView), window.removeEventListener("orientationchange", this.checkIfPaginationInView) }, Ajaxinate.prototype.destroy = function () { return { click: this.removeClickListener, scroll: this.removeScrollListener }[this.settings.method](), this };


/* 
* Endless scroll stops working after filters and/or sorting is applied to a collection so we reload
* When we remove filters on the search results we need to get the original query into to display promo filters etc
* and we need to override scrollRestoration so we can scroll back up to the top of the search page
*/
document.addEventListener('facet:criteria-changed', function() {
    const currentUrl = window.location.href;
    if (currentUrl.match("friendsnyc.com/search")) {
        sessionStorage.setItem("filter-changed", 1);
        sessionStorage.setItem("scrollResto", window.history.scrollRestoration);
        window.history.scrollRestoration = 'manual';
        window.scrollTo(0,0);
    }
    window.location.reload();
});

window.addEventListener('load', function () {
    let filterStatus = sessionStorage.getItem("filter-changed");
    if (filterStatus == 1) {        
        window.scrollTo(0,0);
        sessionStorage.removeItem("filter-changed");
    } else {
        if(window.history.scrollRestoration == 'manual') {
            window.history.scrollRestoration = 'auto';
        }
    }
  });

/*product option select listener*/
function setInnerHTML(elementID, value) {
    let e = document.getElementById(elementID);
    if (e) {
        e.innerHTML = value;
        return 1;
    }
    return 0;
}

/* 
 * Class for adding selected items in a product bundle to the cart
 */

// js/custom-element/behavior/add-bundle-to-cart-button.js
var AddBundleToCartButton = class extends HTMLButtonElement {

    async addSelectedItemsToCart(itemIDs, quantity) {
        var items = [];
        itemIDs.forEach(item => {
            items.push({ quantity: 1, 
                         id: item, 
                         properties: {
                            'UPSELL': 'yes'
                            }
                        })
        });
        this.dispatchEvent(new CustomEvent("line-item-quantity:change:start", { bubbles: true, detail: { newLineQuantity: quantity } }));
        const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
            body: JSON.stringify({
                                    items: items
                                    }
            ),
            credentials: "same-origin",
            method: "POST",
            headers: {
                "Content-type": "application/json"
            }
        });
        const cartContent = await response.json();
        this.dispatchEvent(new CustomEvent("line-item-quantity:change:end", { bubbles: true, detail: { cart: cartContent, newLineQuantity: quantity } }));
        document.getElementById('mini-cart').open = true;  
        return cartContent;
    }
    async _onButtonClicked(event) {
        const checkboxes = document.querySelectorAll("input.bundle-checkbox");
        var items = [];
        for (var i = 0; i < checkboxes.length; i++) {   
            if(checkboxes[i].checked == true) {
                items.push(parseInt(checkboxes[i].value));
            }
        }
        if (items.length > 0 ) {
            event.preventDefault();
            var cartContent = await this.addSelectedItemsToCart(items, items.length);
            this.dispatchEvent(new CustomEvent("cart:updated", {
                                                bubbles: true,
                                                detail: {
                                                cart: cartContent
                                                }
                                                }));
            document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
                                                    bubbles: true,
                                                    detail: {
                                                        cart: cartContent,
                                                        replacementDelay: 750
                                                    }
                                                }));                                       
        } else {
            if (!window.confirm(this.getAttribute("data-message") || "Nothing Selected to add to cart. Please select one or more items")) {
                event.preventDefault();
            }
        }
    }
    connectedCallback() {
        this.addEventListener("click", this._onButtonClicked.bind(this));
    }

    };
window.customElements.define("add-bundle-to-cart-button", AddBundleToCartButton, { extends: "button" });


/* 
 * Code for setting up Event handlers for the product bundle modals
 
window.onload = setupModals;
function setupModals() {
    handleModal("modal_0", "trigger_0", "close-button_0");
    handleModal("modal_1", "trigger_1", "close-button_1");
    handleModal("modal_2", "trigger_2", "close-button_2");
}
function handleModal(mId, tId, cId) {
    const modal = document.getElementById(mId);
    const trigger = document.getElementById(tId);
    const closeButton = document.getElementById(cId);

    function toggleModal() {
        modal.classList.toggle("show-modal");
    }

    function windowOnClick(event) {
        if (event.target === modal) {
            toggleModal();
        }
    }
    if(trigger) {
        trigger.addEventListener("click", toggleModal);
    }
    if (closeButton){
        closeButton.addEventListener("click", toggleModal);
    }
    window.addEventListener("click", windowOnClick);
}
*/

function setThumbScroll() {
    if (window.innerWidth < 999.5) {
        if (document.getElementById("ProductThumbnails")) {
            document.getElementById("ProductThumbnails").classList.add('overflow-scroll-x');
        }
    }
}


function removeBannerNotificationContent() {
    var bannerNotification = document.getElementById('ProductNotification');
    bannerNotification.innerHTML = "";
}

function toggleBannerNotificationVisibility() {
    var bannerNotification = document.getElementById('ProductNotification');
    bannerNotification.classList.toggle('visible-product-notification');
    bannerNotification.classList.toggle('hidden-product-notification');
    setTimeout(removeBannerNotificationContent, 500);

}

//Event listener to make sure we limited items are kept limited 
document.addEventListener('cart:updated', async function(event) {
    var cart = event.detail.cart; // Get the updated content of the cart
    let line = 0; 
    for (const line_item of cart.items) {
        if (line_item.title.includes("Miffy Light XL")) { 
            const elements = document.getElementsByClassName('docapp-cart-with-coupon-summary--summary');
            // Loop through all elements and hide them
            for (let i = 0; i < elements.length; i++) {
                elements[i].style.display = 'none';
            }
        } 
        let qty_limit = 0;
        if (line_item.type.includes("625 Toy - 645 Blind Box") || line_item.type.includes("625 Toy - 649 Sensory and Novelty")) { 
            qty_limit = window.themeVariables.settings.omniLimit;
        } 
        if (line_item.title.includes("Free Unlimited Return for Store Credit or Exchanges") ) {
            qty_limit = 1;
        } 
        if (window.themeVariables.settings.limitedSKUs.includes(line_item.sku)) {
            let limitedSKUsTrimmed = window.themeVariables.settings.limitedSKUs.replace(/\s/g, "")
            let limitedSKUs = limitedSKUsTrimmed.split(',');
            for(let i = 0; i < limitedSKUs.length; i++) {
                let sku_x = limitedSKUs[i].split(':');
                if (sku_x[0] == line_item.sku) {
                    qty_limit = parseInt(sku_x[1])
                }
            }
        }   
        if (qty_limit) {
            if (line_item.quantity > qty_limit ) {
                let id = line_item.key;
                let quantity = qty_limit; 
                document.documentElement.dispatchEvent(new CustomEvent("line-item-quantity:change:start", { bubbles: true, detail: { newLineQuantity: quantity } }));
                const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
                    body: JSON.stringify({ line, id, quantity, sections: ["mini-cart"] }),
                    credentials: "same-origin",
                    method: "POST",
                    headers: {
                    "Content-Type": "application/json"
                    }
                });
                const cartContent = await response.json();
                document.documentElement.dispatchEvent(new CustomEvent("line-item-quantity:change:end", { bubbles: true, detail: { cart: cartContent, newLineQuantity: quantity } }));
                document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
                    bubbles: true,
                    detail: {
                    cart: cartContent
                    }
                }));
                document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
                    bubbles: true,
                    detail: {
                    cart: cartContent,
                    replacementDelay: quantity === 0 ? 600 : 750
                    }
                }));
                var errorMessage = line_item.title + " is limited to  " + qty_limit + " per customer";
                var bannerNotification = document.getElementById('ProductNotification');
                bannerNotification.innerText = errorMessage;
                bannerNotification.classList.toggle('hidden-product-notification');
                bannerNotification.classList.toggle('visible-product-notification');
                setTimeout(toggleBannerNotificationVisibility, 3000);
            }
        }
        line++;
    }
});

function checkLocalityAndDisplayBadge() {
    // Geolocation for ISPU Badge in the mini cart 
    var ispu = document.getElementById('InStorePickUpBadge');
    if (ispu) {
        if ("geolocation" in navigator) { 
            navigator.geolocation.getCurrentPosition((position) => {
                if (isWithinNYCBoroughs(position.coords.latitude, position.coords.longitude)) {
                    ispu.classList.remove('hidden__ispu-badge');
                    ispu.classList.add('visible__ispu-badge');
                } else {
                    ispu.classList.remove('visible__ispu-badge');
                    ispu.classList.add('hidden__ispu-badge');                
                }
            });
        }   
    } 
}

document.addEventListener('cart:updated', async function(event) {
    checkLocalityAndDisplayBadge();
})


let lastScrollTop = 0; // Keeps track of the previous scroll position
const divIds = ["shopify-section-header", "mobile-facet-toolbar"]; 

// Object to store the original "top" values
const originalTopValues = {};

// Save the top values for each div on page load
divIds.forEach(id => {
  const div = document.getElementById(id);
  if (div) {
    const computedStyle = window.getComputedStyle(div);
    originalTopValues[id] = computedStyle.top; // Store the top value
  }
});

function handleScroll() {
  const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

  divIds.forEach(id => {
    const div = document.getElementById(id);
    if (div) {
        if (currentScrollTop > lastScrollTop) {
        // Scrolling down
        div.classList.remove("sticky-header"); // Removes sticky-header class

        // Force re-trigger of slide-away animation
        div.classList.remove("slide-away"); // Temporarily remove
        void div.offsetWidth; // Trigger reflow (forces animation to restart)
        div.classList.add("slide-away"); // Re-add slide-away class

        div.style.position = "relative"; // Sets position to relative
        div.style.top = ""; // Clears the top property
        } else {
        // Scrolling up
        div.classList.remove("slide-away"); // Removes slide-away class
        div.classList.add("sticky-header"); // Adds sticky-header class
        div.style.position = "sticky"; // Re-applies sticky behavior
        div.style.top = originalTopValues[id]; // Restores the original top value
        }
    }
  });

  lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop; // For mobile or negative scroll values
}

function updateScrollListener() {
    window.addEventListener("scroll", handleScroll);
}

// Initial check on page load
updateScrollListener();

// Listen for window resize to dynamically apply/remove scroll logic
window.addEventListener("resize", updateScrollListener);


function initSwipeWrappers(scope = document) {
  scope.querySelectorAll('.product-item__image-wrapper--multiple').forEach(wrapper => {
    // Prevent double-binding (check if already initialized)
    if (wrapper.dataset.swipeBound) return;
    wrapper.dataset.swipeBound = "true";

    const dots = wrapper.nextElementSibling?.querySelectorAll('.dot');
    let swiped = false;

    wrapper.addEventListener('touchstart', e => {
      wrapper.dataset.touchStartX = e.touches[0].clientX;
    });

    wrapper.addEventListener('touchend', e => {
      const startX = parseFloat(wrapper.dataset.touchStartX);
      const endX = e.changedTouches[0].clientX;

      if (Math.abs(endX - startX) > 50) { // swipe threshold
        swiped = !swiped;
        wrapper.classList.toggle('swiped', swiped);

        // Update dots
        if (dots) {
          dots.forEach(dot => dot.classList.remove('active'));
          dots[swiped ? 1 : 0].classList.add('active');
        }
      }
    });
  });
}

// Run once on DOM ready
document.addEventListener("DOMContentLoaded", function() {
  initSwipeWrappers();

  // Ajaxinate with callback to re-init
  new Ajaxinate({
    offset: 1000,
    callback: (responseXML) => {
      // Limit scope to the newly loaded container for efficiency
      const newContainer = responseXML.querySelector('#AjaxinateContainer');
      if (newContainer) {
        initSwipeWrappers(document); // rebind within whole doc
      }
    }
  });
});


document.addEventListener('cart:updated', handleCartUpdated);

const DEBUG = false; // toggle debug output
let giftAddFailed = false; // prevents infinite retry if add fails

async function handleCartUpdated(event) {

  if (DEBUG) console.group('🛒 cart:updated handler fired');

    const cart = event?.detail?.cart || (await fetch('/cart.js').then(r => r.json()));
    if (!cart) {
      if (DEBUG) console.warn('⚠️ No cart data available.');
      console.groupEnd();
      return;
    }
    if (DEBUG) {
        console.log("cart: ")
        console.log(cart);
    }
  try {
    const settings = window.themeVariables?.settings || {};
    const threshold = Number(settings.gwpAmount) || 0;
    const giftType = settings.gwpType || 'none';
    const giftVendor = settings.gwpVendor || 'none';
    const giftVariantId = settings.gwpProduct || null;

    if (DEBUG) {
      console.log('themeVariables.settings:', settings);
      console.log('Parsed settings → threshold:', threshold, '| giftType:', giftType, '| giftVariantId:', giftVariantId, '| giftVendor:', giftVendor );
    }

    const featureEnabled = threshold > 0;
    if (!featureEnabled) {
      if (DEBUG) console.log('❌ Feature disabled. Exiting.');
      console.groupEnd();
      return;
    }

    const cart = event?.detail?.cart || (await fetch('/cart.js').then(r => r.json()));
    if (!cart) {
      if (DEBUG) console.warn('⚠️ No cart data available.');
      console.groupEnd();
      return;
    }


    const hasGift = cart.items.some(i => i.id === giftVariantId);
    var qualifies = false; 
    if (giftType != 'none') {
    // Substring match on product_type
    const matchStr = giftType.toLowerCase();
    const qualifyingTypeValue = cart.items
        .filter(i =>
                    matchStr === 'any' ||
                    (i.product_type && i.product_type.toLowerCase().includes(matchStr.toLowerCase()))
                ).reduce((sum, i) => sum + (i.final_line_price / 100), 0);
     qualifies = qualifyingTypeValue >= threshold;
       if (DEBUG) {
        console.log('Totals → qualifyingTypeValue:', qualifyingTypeValue, '| threshold:', threshold);
        console.log('Qualification result → qualifies:', qualifies);
        }
    }
   if (giftVendor != 'none') {
    // Substring match on vendor
    const matchStr = giftVendor.toLowerCase();
    const qualifyingVendorValue = cart.items
        .filter(i =>
                    matchStr === 'any' ||
                    (i.vendor && i.vendor.toLowerCase().includes(matchStr.toLowerCase()))
                ).reduce((sum, i) => sum + (i.final_line_price / 100), 0);
     qualifies = qualifyingVendorValue >= threshold;
       if (DEBUG) {
        console.log('Totals → qualifyingVendorValue:', qualifyingVendorValue, '| threshold:', threshold);
        console.log('Qualification result → qualifies:', qualifies);
        }
    }    
  
    
    // --- ADD GIFT ---
    if (qualifies && !hasGift && !giftAddFailed) {
      if (DEBUG) console.log('🎁 Qualifies but gift not in cart — attempting to add gift...');
      let addOk = false;
      try {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: giftVariantId, quantity: 1 }] })
        });

        const text = await addRes.text();
        if (!addRes.ok) {
          giftAddFailed = true;
          if (DEBUG) console.error('🚫 Gift add failed:', addRes.status, text);
          console.warn('Gift add failed — preventing retry.');
          console.groupEnd();
          return;
        }
        addOk = true;
      } catch (err) {
        giftAddFailed = true;
        if (DEBUG) console.error('🚫 Gift add error:', err);
        console.warn('Gift add failed — preventing retry.');
        console.groupEnd();
        return;
      }

      if (!addOk) return;

      if (DEBUG) console.log('✅ Gift added, fetching updated cart…');
      const updatedCart = await fetch('/cart.js').then(r => r.json());

      if (window.location.pathname === '/cart') {
        if (DEBUG) console.log('🔁 Reloading cart page to reflect update…');
        window.location.reload();
      } else {
        if (DEBUG) console.log('📣 Dispatching cart refresh events with updated cart');
        document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
          bubbles: true,
          detail: { cart: updatedCart }
        }));
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: { cart: updatedCart, replacementDelay: 750 }
        }));
      }

      console.groupEnd();
      return;
    }

    // --- REMOVE GIFT ---
    if (!qualifies && hasGift) {
      if (DEBUG) console.log('🗑️ No longer qualifies but gift is in cart — removing gift...');
      const giftItem = cart.items.find(i => i.id === giftVariantId);
      if (giftItem) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: giftItem.key, quantity: 0 })
          });
        } catch (err) {
          if (DEBUG) console.error('🚫 Gift remove failed:', err);
        }

        if (DEBUG) console.log('✅ Gift removed, fetching updated cart…');
        const updatedCart = await fetch('/cart.js').then(r => r.json());

        if (window.location.pathname === '/cart') {
          if (DEBUG) console.log('🔁 Reloading cart page to reflect removal…');
          window.location.reload();
        } else {
          if (DEBUG) console.log('📣 Dispatching cart refresh events with updated cart');
          document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
            bubbles: true,
            detail: { cart: updatedCart }
          }));
          document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: { cart: updatedCart, replacementDelay: 750 }
          }));
        }
      }

      console.groupEnd();
      return;
    }

    if (DEBUG) console.log('✅ No change needed — state is consistent.');
  } catch (err) {
    if (DEBUG) console.error('💥 Error in cart handler:', err);
  }

  console.groupEnd();
}

// --- Run once on page load ---
document.addEventListener('DOMContentLoaded', () => {
  if (DEBUG) console.log('🧠 Running GWP check on page load...');
  handleCartUpdated();
});




// --- Run once on page load ---
document.addEventListener('DOMContentLoaded', () => {
  if (DEBUG) console.log('🧠 Running GWP check on page load...');
  handleCartUpdated();
});

document.addEventListener('cart:updated', () => {
  const text = document.querySelector('.shipping-bar__text');
  if (!text) return;

  const current = text.textContent.trim();
  if (text.dataset.last !== current) {
    text.dataset.last = current;

    text.classList.remove('changed');
    void text.offsetWidth; // force reflow
    text.classList.add('changed');
  }
});
